import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl, formatApiError } from "../lib/api";
import { useAuth } from "../auth/AuthContext";
import { AdminLayout } from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { FileUpload } from "../components/FileUpload";
import { PhoneInput } from "../components/PhoneInput";
import {
  ChevronLeft,
  UserCircle2,
  Sparkles,
  Mail,
  ShieldCheck,
  Loader2,
  User as UserIcon,
  MapPin,
  Building2,
  ExternalLink,
  KeyRound,
  Instagram,
  MessageCircle,
  FileBadge,
  Facebook,
  Linkedin,
  Youtube,
  Send,
  Twitter,
  Music2,
  Share2,
  Globe,
  Plus,
  Boxes,
  SprayCan,
  AlertTriangle,
  CheckCircle2,
  PackageX,
  X as XIcon,
} from "lucide-react";
import { ROLE_LABEL } from "../lib/format";
import { useI18n } from "../i18n/I18nContext";
import { LanguageToggle } from "../components/LanguageToggle";
import { LeaderMaterials } from "../components/LeaderMaterials";
import { titleCase } from "../lib/textCase";
import { toast } from "sonner";

const PAYMENT_METHODS = [];  // UI removida do perfil (mantido vazio p/ retrocompat)

/** Catálogo de redes sociais disponíveis no cadastro. */
const SOCIAL_NETWORKS = [
  { id: "facebook", label: "Facebook", icon: Facebook, color: "#1877f2", prefix: "facebook.com/", placeholder: "usuario" },
  { id: "tiktok", label: "TikTok", icon: Music2, color: "#000000", prefix: "@", placeholder: "usuario" },
  { id: "youtube", label: "YouTube", icon: Youtube, color: "#ff0000", prefix: "youtube.com/@", placeholder: "canal" },
  { id: "linkedin", label: "LinkedIn", icon: Linkedin, color: "#0a66c2", prefix: "linkedin.com/in/", placeholder: "perfil" },
  { id: "x", label: "X (Twitter)", icon: Twitter, color: "#000000", prefix: "@", placeholder: "usuario" },
  { id: "telegram", label: "Telegram", icon: Send, color: "#229ed9", prefix: "t.me/", placeholder: "usuario" },
  { id: "threads", label: "Threads", icon: Share2, color: "#000000", prefix: "@", placeholder: "usuario" },
  { id: "website", label: "Site / Blog", icon: Globe, color: "#0ea5e9", prefix: "https://", placeholder: "exemplo.com" },
];

export default function Profile() {
  const { user, setUser } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const isCollab = user?.role === "collaborator";
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState({
    name: "",
    phone: "",
    whatsapp: "",
    instagram: "",
    social_links: {},
    address: {
      street: "",
      apt_number: "",
      city: "",
      state: "",
      zip_code: "",
    },
    photo_path: "",
    ssn: "",
    payment_method: "",
    payment_handle: "",
    current_password: "",
    new_password: "",
  });
  const [busy, setBusy] = useState(false);
  const [company, setCompany] = useState(null);
  const [showSsn, setShowSsn] = useState(false);

  useEffect(() => {
    if (user)
      setForm((f) => ({
        ...f,
        name: user.name || "",
        phone: user.phone || "",
        whatsapp: user.whatsapp || "",
        instagram: user.instagram || "",
        social_links:
          (typeof user.social_links === "object" && user.social_links) || {},
        address:
          typeof user.address === "object" && user.address
            ? { ...f.address, ...user.address }
            : { ...f.address, street: user.address || "" },
        photo_path: user.photo_path || "",
        ssn: user.ssn || "",
        payment_method: user.payment_method || "",
        payment_handle: user.payment_handle || "",
      }));
  }, [user]);

  // Carrega dados da empresa pra exibir como summary (admin only)
  useEffect(() => {
    if (!isAdmin) return;
    api
      .get("/company-settings")
      .then((r) => setCompany(r.data || null))
      .catch(() => {});
  }, [isAdmin]);

  function set(k, v) {
    let val = v;
    if (k === "name") val = titleCase(v);
    setForm((f) => ({ ...f, [k]: val }));
  }
  function setAddr(k, v) {
    setForm((f) => ({ ...f, address: { ...f.address, [k]: v } }));
  }

  async function save() {
    setBusy(true);
    try {
      const body = {
        name: form.name,
        phone: form.phone,
        whatsapp: form.whatsapp || null,
        instagram: form.instagram || null,
        social_links: form.social_links || {},
        address: form.address,
        photo_path: form.photo_path,
        ssn: form.ssn || null,
        payment_method: form.payment_method || null,
        payment_handle: form.payment_handle || null,
      };
      if (form.new_password) {
        if (!form.current_password) {
          toast.error(t("Informe a senha atual para trocar"));
          setBusy(false);
          return;
        }
        body.current_password = form.current_password;
        body.new_password = form.new_password;
      }
      const { data } = await api.patch("/auth/me", body);
      setUser(data);
      set("current_password", "");
      set("new_password", "");
      toast.success(t("Perfil atualizado"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  }

  const body = (
    <div className="max-w-3xl mx-auto space-y-5">
      {/* Hero */}
      <div className="flex items-start gap-4">
        <div className="w-24 h-24 rounded-2xl bg-slate-100 overflow-hidden flex items-center justify-center text-slate-400 border border-slate-200 shadow-sm flex-shrink-0">
          {form.photo_path ? (
            <img
              src={fileUrl(form.photo_path)}
              alt=""
              className="w-full h-full object-cover"
              data-testid="profile-photo-preview"
            />
          ) : (
            <UserCircle2 size={48} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold">
            {t("Minha conta")}
          </div>
          <h1 className="text-2xl md:text-3xl font-bold truncate flex items-center gap-2">
            <span className="truncate">{form.name || user?.name}</span>
            {user?.auth_provider === "google" && (
              <span
                title="Conta conectada com Google"
                data-testid="profile-google-linked-badge"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-slate-200 shadow-sm text-[10px] font-bold text-slate-600 tracking-wide uppercase flex-shrink-0"
              >
                <svg width="11" height="11" viewBox="0 0 18 18" aria-hidden="true">
                  <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" />
                  <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" />
                  <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" />
                  <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z" />
                </svg>
                Google
              </span>
            )}
          </h1>
          <div className="text-xs text-slate-500 flex items-center gap-2 mt-1 flex-wrap">
            <ShieldCheck size={12} className="text-emerald-500" />
            {t(ROLE_LABEL[user?.role])}
            <span className="text-slate-300">·</span>
            <Mail size={12} /> {user?.email}
          </div>
        </div>
      </div>

      {/* Materiais da equipe — somente para LÍDERES (colaborador) */}
      {isCollab && <LeaderMaterials />}

      {/* Idioma do sistema */}
      <Section
        icon={Globe}
        title={t("Idioma do sistema")}
        subtitle={t("Escolha o idioma da interface. A preferência fica salva no seu perfil.")}
      >
        <LanguageToggle persist testId="profile-language-toggle" />
      </Section>

      {/* Foto */}
      <Section icon={UserIcon} title={t("Foto de reconhecimento")} subtitle={t("Usada nos crachás, mapas e relatórios. Mesma foto que os colaboradores usam.")}>
        <FileUpload
          folder="profile"
          multiple={false}
          value={form.photo_path ? [form.photo_path] : []}
          onChange={(v) => set("photo_path", v[0] || "")}
          testId="profile-photo"
        />
      </Section>

      {/* Identidade */}
      <Section icon={UserIcon} title={t("Dados pessoais")}>
        <Field label={t("Nome completo")}>
          <input
            data-testid="profile-name"
            className="cm-input"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
          />
        </Field>
        <Field label={t("Telefone (US)")}>
          <PhoneInput
            testId="profile-phone"
            value={form.phone}
            onChange={(v) => set("phone", v)}
          />
        </Field>
        <Field
          label={
            <span className="flex items-center gap-1">
              <FileBadge size={12} /> SSN ou ITIN Number
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-normal ml-1">
                {t("(criptografado · só você vê)")}
              </span>
            </span>
          }
        >
          <div className="relative">
            <input
              data-testid="profile-ssn"
              type={showSsn ? "text" : "password"}
              className="cm-input pr-20 font-mono"
              value={form.ssn}
              onChange={(e) => set("ssn", e.target.value.replace(/\s/g, ""))}
              placeholder="XXX-XX-XXXX"
            />
            <button
              type="button"
              onClick={() => setShowSsn((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-blue-600 hover:text-blue-700 font-semibold"
            >
              {showSsn ? t("ocultar") : t("mostrar")}
            </button>
          </div>
        </Field>
      </Section>

      {/* Redes sociais */}
      <SocialSection form={form} setForm={setForm} />

      {/* Endereço */}
      <Section icon={MapPin} title={t("Endereço")}>
        <Field label={t("Rua e número")}>
          <input
            data-testid="profile-street"
            className="cm-input"
            value={form.address.street}
            onChange={(e) => setAddr("street", e.target.value)}
            placeholder="Ex.: 123 Main St"
          />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr,1fr,140px,120px] gap-3">
          <Field label={t("Apto / Suite")}>
            <input
              className="cm-input"
              value={form.address.apt_number}
              onChange={(e) => setAddr("apt_number", e.target.value)}
              placeholder="Apt 4B"
            />
          </Field>
          <Field label={t("Cidade")}>
            <input
              className="cm-input"
              value={form.address.city}
              onChange={(e) => setAddr("city", e.target.value)}
            />
          </Field>
          <Field label={t("Estado")}>
            <input
              className="cm-input"
              value={form.address.state}
              onChange={(e) => setAddr("state", e.target.value.toUpperCase().slice(0, 2))}
              placeholder="MA"
              maxLength={2}
            />
          </Field>
          <Field label="ZIP">
            <input
              className="cm-input"
              value={form.address.zip_code}
              onChange={(e) => setAddr("zip_code", e.target.value)}
              placeholder="02101"
              maxLength={10}
            />
          </Field>
        </div>
      </Section>

      {/* Dados da empresa (read-only summary, admin only) */}
      {isAdmin && (
        <Section
          icon={Building2}
          title={t("Dados da empresa")}
          subtitle={t("Informações que aparecem nos invoices, recibos e PDFs.")}
          action={
            <button
              onClick={() => nav("/configuracoes/empresa")}
              className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700"
              data-testid="profile-edit-company"
            >
              {t("Editar")} <ExternalLink size={11} />
            </button>
          }
        >
          {company === null ? (
            <div className="text-sm text-slate-400">{t("Carregando…")}</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4 text-sm">
              <Info label={t("Razão social")} value={company.company_name || "—"} />
              <Info label="EIN / Tax ID" value={company.tax_id || "—"} />
              <Info label={t("E-mail")} value={company.email || "—"} />
              <Info label={t("Telefone")} value={company.phone || "—"} />
              <Info
                label={t("Endereço")}
                value={
                  [
                    company.address,
                    company.city,
                    company.state,
                    company.zip_code,
                  ]
                    .filter(Boolean)
                    .join(", ") || "—"
                }
                wide
              />
            </div>
          )}
        </Section>
      )}

      {/* Login com Google (vincular/desvincular) */}
      <Section icon={ShieldCheck} title={t("Login com Google")}>
        <GoogleLinkRow user={user} setUser={setUser} />
      </Section>

      {/* Senha */}
      <Section icon={KeyRound} title={t("Trocar senha (opcional)")}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={t("Senha atual")}>
            <input
              data-testid="profile-current-pwd"
              type="password"
              className="cm-input"
              value={form.current_password}
              onChange={(e) => set("current_password", e.target.value)}
            />
          </Field>
          <Field label={t("Nova senha")}>
            <input
              data-testid="profile-new-pwd"
              type="password"
              className="cm-input"
              value={form.new_password}
              onChange={(e) => set("new_password", e.target.value)}
            />
          </Field>
        </div>
      </Section>

      <div className="flex justify-end pt-2">
        <Button
          data-testid="profile-save"
          onClick={save}
          disabled={busy}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {busy && <Loader2 size={14} className="animate-spin mr-2" />}
          {t("Salvar alterações")}
        </Button>
      </div>
    </div>
  );

  if (isCollab) {
    return (
      <div className="min-h-screen bg-[var(--cm-bg)] pb-10">
        <header className="bg-[var(--cm-ink)] text-white px-5 pt-5 pb-6 rounded-b-3xl relative overflow-hidden">
          <div className="absolute inset-0 cm-grid-bg opacity-10" />
          <div className="relative z-10 flex items-center justify-between">
            <button
              onClick={() => nav("/mobile")}
              data-testid="profile-back"
              className="text-sm flex items-center gap-1 text-blue-200 hover:text-white"
            >
              <ChevronLeft size={16} /> {t("Voltar")}
            </button>
            <div className="text-[11px] uppercase tracking-widest text-blue-200 flex items-center gap-1">
              <Sparkles size={12} /> {t("Perfil")}
            </div>
          </div>
        </header>
        <main className="p-4 -mt-2 relative z-10">{body}</main>
      </div>
    );
  }

  return <AdminLayout>{body}</AdminLayout>;
}

/**
 * LeaderMaterials foi movido para um componente reutilizável
 * (também usado na tela inicial do colaborador).
 */

function Section({ icon: Icon, title, subtitle, action, children }) {
  return (
    <section className="cm-card p-5">
      <header className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
            <Icon size={16} />
          </div>
          <div>
            <h3 className="font-semibold text-slate-800 leading-tight">{title}</h3>
            {subtitle && (
              <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>
            )}
          </div>
        </div>
        {action}
      </header>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium mb-1 text-slate-700">{label}</span>
      {children}
    </label>
  );
}

function Info({ label, value, wide }) {
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
        {label}
      </div>
      <div className="text-slate-800 truncate" title={value}>
        {value}
      </div>
    </div>
  );
}


/**
 * Redes sociais com toggle. Lista pílulas para cada rede; ao ativar uma,
 * aparece o input correspondente abaixo. Inclui WhatsApp e Instagram como
 * fixos (campos dedicados) + redes opcionais via `social_links` dict.
 */
function SocialSection({ form, setForm }) {
  const { t } = useI18n();
  function toggleNetwork(id) {
    setForm((f) => {
      const cur = { ...(f.social_links || {}) };
      if (id in cur) {
        delete cur[id];
      } else {
        cur[id] = "";
      }
      return { ...f, social_links: cur };
    });
  }
  function setLink(id, v) {
    setForm((f) => ({
      ...f,
      social_links: { ...(f.social_links || {}), [id]: v },
    }));
  }
  function toggleFixed(field) {
    setForm((f) => {
      if (f[`__show_${field}`]) {
        // hide: keep value but mark hidden (or wipe? wipe to simplify)
        return { ...f, [`__show_${field}`]: false, [field]: "" };
      }
      return { ...f, [`__show_${field}`]: true };
    });
  }

  const showWa = !!form.whatsapp || form.__show_whatsapp;
  const showIg = !!form.instagram || form.__show_instagram;

  return (
    <section className="cm-card p-5" data-testid="profile-socials-section">
      <header className="flex items-start gap-2.5 mb-3">
        <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
          <Share2 size={16} />
        </div>
        <div>
          <h3 className="font-semibold text-slate-800 leading-tight">{t("Redes sociais")}</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {t("Marque as redes que você usa — os campos aparecem conforme você seleciona.")}
          </p>
        </div>
      </header>

      {/* Pílulas: WhatsApp + Instagram + redes opcionais */}
      <div className="flex flex-wrap gap-2 mb-3">
        <NetworkPill
          icon={MessageCircle}
          label="WhatsApp"
          color="#25d366"
          active={showWa}
          onClick={() => toggleFixed("whatsapp")}
          testId="pill-whatsapp"
        />
        <NetworkPill
          icon={Instagram}
          label="Instagram"
          color="#e1306c"
          active={showIg}
          onClick={() => toggleFixed("instagram")}
          testId="pill-instagram"
        />
        {SOCIAL_NETWORKS.map((n) => (
          <NetworkPill
            key={n.id}
            icon={n.icon}
            label={n.label}
            color={n.color}
            active={n.id in (form.social_links || {})}
            onClick={() => toggleNetwork(n.id)}
            testId={`pill-${n.id}`}
          />
        ))}
      </div>

      {/* Inputs aparecem somente para as redes selecionadas */}
      <div className="space-y-3">
        {showWa && (
          <Field label={<><MessageCircle size={12} className="inline" /> WhatsApp</>}>
            <PhoneInput
              testId="profile-whatsapp"
              value={form.whatsapp}
              onChange={(v) => setForm((f) => ({ ...f, whatsapp: v }))}
            />
          </Field>
        )}
        {showIg && (
          <Field label={<><Instagram size={12} className="inline" /> Instagram</>}>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                @
              </span>
              <input
                data-testid="profile-instagram"
                className="cm-input pl-7"
                value={(form.instagram || "").replace(/^@/, "")}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    instagram: e.target.value.replace(/^@/, ""),
                  }))
                }
                placeholder="seu_usuario"
              />
            </div>
          </Field>
        )}
        {SOCIAL_NETWORKS.filter((n) => n.id in (form.social_links || {})).map(
          (n) => (
            <Field
              key={n.id}
              label={
                <span className="flex items-center gap-1">
                  <n.icon size={12} style={{ color: n.color }} /> {n.label}
                </span>
              }
            >
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">
                  {n.prefix}
                </span>
                <input
                  data-testid={`profile-social-${n.id}`}
                  className="cm-input"
                  style={{ paddingLeft: `${n.prefix.length * 7 + 14}px` }}
                  value={form.social_links[n.id] || ""}
                  onChange={(e) => setLink(n.id, e.target.value)}
                  placeholder={n.placeholder}
                />
              </div>
            </Field>
          ),
        )}
        {!showWa &&
          !showIg &&
          Object.keys(form.social_links || {}).length === 0 && (
            <p className="text-xs text-slate-400 italic">
              <Plus size={11} className="inline mr-1" />
              {t("Selecione acima as redes que você usa.")}
            </p>
          )}
      </div>
    </section>
  );
}

function NetworkPill({ icon: Icon, label, color, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      data-active={active ? "true" : "false"}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
        active
          ? "bg-slate-900 text-white border-slate-900 shadow-sm"
          : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
      }`}
    >
      <Icon size={13} style={{ color: active ? color : undefined }} />
      {label}
    </button>
  );
}

function GoogleLinkRow({ user, setUser }) {
  const { t } = useI18n();
  const linked = user?.auth_provider === "google";
  const [busy, setBusy] = useState(false);

  async function connectGoogle() {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    try {
      localStorage.setItem("cm_google_link_pending", "1");
    } catch {
      // ignore
    }
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href =
      `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }

  async function disconnectGoogle() {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/google/unlink");
      setUser({ ...user, auth_provider: data.auth_provider });
      toast.success(t("Conta Google desconectada"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 rounded-xl border border-slate-200 bg-slate-50/60">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center flex-shrink-0">
          <svg width="20" height="20" viewBox="0 0 18 18" aria-hidden="true">
            <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" />
            <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" />
            <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" />
            <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z" />
          </svg>
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-800">Google</div>
          <div className="text-xs text-slate-500">
            {linked
              ? t("Você pode entrar usando o botão 'Entrar com Google' na tela de login.")
              : t("Conecte seu Google para entrar com 1 clique (mesmo e-mail).")}
          </div>
        </div>
      </div>
      <div className="flex-shrink-0">
        {linked ? (
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={disconnectGoogle}
            data-testid="profile-google-unlink"
            className="text-red-600 hover:bg-red-50 border-red-200"
          >
            {busy && <Loader2 size={14} className="animate-spin mr-2" />}
            {t("Desconectar")}
          </Button>
        ) : (
          <Button
            type="button"
            onClick={connectGoogle}
            data-testid="profile-google-link"
            className="bg-white border border-slate-300 text-slate-700 hover:bg-slate-100"
          >
            {t("Conectar com Google")}
          </Button>
        )}
      </div>
    </div>
  );
}

